import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { devError } from "@/lib/logger";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { gameService } from "@/services";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CryptoCard from "@/components/game/cards/CryptoCard";
import {
  Eye, ArrowLeft, Crown, Zap, TrendingUp, Sparkles, Lock, DollarSign
} from "lucide-react";

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;
const SPECTATOR_DELAY_MS = 10000; // 10-second delay for anti-cheating

export default function SpectatorPage() {
  const { t } = useTranslation();
  usePageTitle("Spectate");
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [delayedGame, setDelayedGame] = useState(null);
  const socketRef = useRef(null);
  const gameQueueRef = useRef([]);

  useEffect(() => {
    // Fetch initial state
    gameService.getGame(gameId)
      .then((data) => { setGame(data); setDelayedGame(data); })
      .catch(err => { devError("Failed to load spectator game:", err); });

    // Connect socket for live updates
    const s = io(SOCKET_URL, { path: "/api/socket.io", transports: ["websocket", "polling"] });
    s.on("connect", () => { s.emit("spectate_game", { game_id: gameId }); });
    s.on("game_update", (data) => {
      setGame(data); // Immediate state for reference
      // Queue delayed display
      const showAt = Date.now() + SPECTATOR_DELAY_MS;
      gameQueueRef.current.push({ data, showAt });
    });
    socketRef.current = s;

    // Process delayed queue
    const interval = setInterval(() => {
      const now = Date.now();
      const queue = gameQueueRef.current;
      while (queue.length > 0 && queue[0].showAt <= now) {
        const item = queue.shift();
        setDelayedGame(item.data);
      }
    }, 500);

    return () => {
      clearInterval(interval);
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    };
  }, [gameId]);

  const display = delayedGame || game;

  if (!display) return (
    <div className="min-h-screen flex items-center justify-center bg-[#E3F2FD] dark:bg-[#0a1428]">
      <div className="text-[#1A237E] dark:text-[#E3F2FD] font-bold animate-pulse">{t("spectator.loading")}</div>
    </div>
  );

  const currentPlayer = display.players[display.current_player_index];
  const isFinished = display.status === "finished";
  const sortedPlayers = [...display.players].map((p, i) => ({ ...p, idx: i })).sort((a, b) => b.crypto_value - a.crypto_value);

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white" id="main-content" role="main" tabIndex={-1} data-testid="spectator-page">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-white/70 hover:text-white" data-testid="spectator-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#38B6FF]" />
            <h1 className="text-lg font-bold" style={{ fontFamily: "Fredoka" }}>{t("spectator.title")}</h1>
          </div>
          <Badge className="bg-red-500/20 text-red-400 text-[10px] animate-pulse">{t("spectator.badge.live")}</Badge>
          <Badge className="bg-white/10 dark:bg-slate-900/10 text-white/50 text-[10px]">{t("spectator.badge.delay")}</Badge>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Badge variant="outline" className="border-[#38B6FF]/50 text-[#38B6FF]">{t("spectator.round", { round: display.round_number })}</Badge>
          <Badge variant="outline" className="border-[#FFC107]/50 text-[#FFC107]">
            <Zap className="w-3 h-3 mr-1" /> {display.market_melee_count}/3
          </Badge>
          {!isFinished && <span className="text-white/60">{t("spectator.turn")} <strong className="text-white">{currentPlayer.name}</strong></span>}
        </div>
      </header>

      <div className="flex flex-col lg:flex-row gap-4 p-4 max-w-7xl mx-auto">
        {/* Scoreboard */}
        <div className="lg:w-80 space-y-3">
          <h2 className="text-sm font-bold text-white/60 uppercase tracking-wider flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#FFC107]" /> {t("spectator.scoreboard")}
          </h2>
          {sortedPlayers.map((p, rank) => (
            <div key={p.idx} className={`rounded-xl p-4 border transition-all ${
              p.idx === display.current_player_index ? "border-[#38B6FF]/50 bg-[#38B6FF]/10" :
              isFinished && p.idx === display.winner ? "border-[#FFC107]/50 bg-[#FFC107]/10" :
              "border-white/10 bg-white/5 dark:bg-slate-900/5"
            }`} data-testid={`spectator-player-${p.idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${rank === 0 ? "text-[#FFC107]" : "text-white/30"}`}>#{rank + 1}</span>
                  {isFinished && p.idx === display.winner && <Crown className="w-4 h-4 text-[#FFC107]" />}
                  <span className="font-bold">{p.name}</span>
                </div>
                <span className="text-lg font-bold text-[#38B6FF]" style={{ fontFamily: "Fredoka" }}>${p.crypto_value}</span>
              </div>
              {/* Portfolio preview */}
              <div className="flex gap-1 flex-wrap mb-2">
                {p.portfolio.map((c) => (
                  <span key={c.id} className="text-[10px] px-1.5 py-0.5 rounded-md font-bold bg-white/10 dark:bg-slate-900/10"
                    style={{ color: { BTC: "#F7931A", ETH: "#627EEA", ADA: "#0033AD", DOGE: "#C2A633", SOL: "#9945FF", AVAX: "#E84142", MATIC: "#8247E5", NEAR: "#00C08B", TON: "#0098EA" }[c.symbol] || "#fff" }}>
                    {c.symbol} ${c.current_value}
                  </span>
                ))}
                {p.portfolio.length === 0 && <span className="text-[10px] text-white/20">{t("spectator.player.noCrypto")}</span>}
              </div>
              <div className="flex items-center gap-3 text-xs text-white/40">
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${p.stablecoin_total}</span>
                {p.stablecoin_locked > 0 && <span className="flex items-center gap-1 text-red-400"><Lock className="w-3 h-3" /> ${p.stablecoin_locked}</span>}
                <span>{t("spectator.player.cardsInHand", { count: p.hand?.length || 0 })}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Game Info + Event Log */}
        <div className="flex-1 space-y-4">
          {isFinished && (
            <div className="rounded-xl p-6 text-center bg-gradient-to-r from-[#FFC107]/20 to-[#FFC107]/5 border border-[#FFC107]/30" data-testid="spectator-game-over">
              <Crown className="w-12 h-12 mx-auto mb-2 text-[#FFC107]" />
              <h2 className="text-2xl font-bold text-[#FFC107]" style={{ fontFamily: "Fredoka" }}>
                {t("spectator.gameOver.wins", { name: display.players[display.winner]?.name })}
              </h2>
              <p className="text-white/60">{t("spectator.gameOver.cryptoValue", { value: display.players[display.winner]?.crypto_value })}</p>
            </div>
          )}

          {/* Deck Info */}
          <div className="flex gap-3">
            <div className="flex-1 rounded-xl p-3 bg-pink-500/10 border border-pink-500/20 text-center">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-pink-400" />
              <p className="text-xs text-white/50">{t("spectator.deck.crypto")}</p>
              <p className="text-xl font-bold text-pink-400">{display.crypto_deck_count}</p>
            </div>
            <div className="flex-1 rounded-xl p-3 bg-blue-500/10 border border-blue-500/20 text-center">
              <Sparkles className="w-5 h-5 mx-auto mb-1 text-blue-400" />
              <p className="text-xs text-white/50">{t("spectator.deck.effect")}</p>
              <p className="text-xl font-bold text-blue-400">{display.effect_deck_count}</p>
            </div>
            <div className="flex-1 rounded-xl p-3 bg-red-500/10 border border-red-500/20 text-center">
              <Zap className="w-5 h-5 mx-auto mb-1 text-red-400" />
              <p className="text-xs text-white/50">{t("spectator.deck.melee")}</p>
              <p className="text-xl font-bold text-red-400">{display.market_melee_count}/3</p>
            </div>
          </div>

          {/* Event Log */}
          <div className="rounded-xl border border-white/10 bg-white/5 dark:bg-slate-900/5 p-4 max-h-[50vh] overflow-y-auto scroll-thin">
            <h3 className="text-sm font-bold text-white/60 mb-3 uppercase tracking-wider">{t("spectator.eventLog")}</h3>
            <div className="space-y-1">
              {(display.event_log || []).map((ev, i) => (
                <div key={`ev-${i}-${ev.message?.slice(0,20) || ''}`} className={`text-xs px-2 py-1.5 rounded-lg ${
                  ev.type === "melee" ? "bg-red-500/10 text-red-300" :
                  ev.type === "event" ? "bg-amber-500/10 text-amber-300" :
                  ev.type === "system" ? "bg-blue-500/10 text-blue-300" :
                  ev.type === "buy" ? "bg-green-500/10 text-green-300" :
                  ev.type === "sell" ? "bg-orange-500/10 text-orange-300" :
                  "bg-white/5 dark:bg-slate-900/5 text-white/40"
                }`}>
                  {ev.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
